import { useId, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { adminUploadImage, formatError } from "@/lib/api";

const inputCls = "field-underline w-full font-dm text-[#FAFAFA] py-2.5 text-sm placeholder:text-[#A3A3A3]/50";
const labelCls = "font-oswald uppercase tracking-[0.2em] text-[11px] text-[#A3A3A3] block mb-1";

export default function ImageField({
  label = "Image",
  value = "",
  onChange,
  folder = "general",
  authHeader = {},
  required = false,
}) {
  const id = useId();
  const [uploading, setUploading] = useState(false);
  const enforceRequired = required && folder !== "sponsors";

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setUploading(true);
    try {
      const uploaded = await adminUploadImage(file, folder, authHeader);
      onChange(uploaded.url);
      toast.success("Image uploaded.");
    } catch (error) {
      toast.error(formatError(error, "Image upload failed."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="sm:col-span-2">
      <label className={labelCls}>{label}</label>

      {value && (
        <div className="relative border border-white/10 bg-black/30 mb-3 overflow-hidden">
          <img src={value} alt="Preview" className="w-full h-40 object-contain" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-2 right-2 p-2 bg-black/80 text-[#A3A3A3] hover:text-red-400 transition-colors"
            aria-label="Remove image"
          >
            <X size={15} />
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <input
          type="url"
          required={enforceRequired}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Image URL or upload a file"
          className={inputCls}
        />
        <label
          htmlFor={id}
          className="shrink-0 cursor-pointer font-oswald uppercase tracking-[0.15em] text-xs border border-white/20 text-[#A3A3A3] px-4 py-2.5 hover:border-[#E85A24] hover:text-[#E85A24] transition-colors duration-300 inline-flex items-center justify-center gap-2"
        >
          <ImagePlus size={14} /> {uploading ? "Uploading…" : "Upload"}
        </label>
        <input
          id={id}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={upload}
          disabled={uploading}
          className="sr-only"
        />
      </div>
      <p className="font-dm text-[11px] text-[#A3A3A3]/60 mt-2">JPG, PNG, WebP or GIF. Maximum 8 MB and 6000 × 6000 pixels.</p>
    </div>
  );
}
